import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import StepIndicator from "@/components/StepIndicator";
import CustomerStep from "@/components/steps/CustomerStep";
import MeasurementsStep from "@/components/steps/MeasurementsStep";
import ItemsStep from "@/components/steps/ItemsStep";
import CheckoutStep from "@/components/steps/CheckoutStep";
import ConfirmationStep from "@/components/steps/ConfirmationStep";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialOrder = {
  customer: { name: "", contact: "", address: "" },
  measurements: {
    shoulder: 0, bust: 0, hips: 0, length: 0,
    sleeve_length: 0, armhole: 0, cuff: 0,
    button_placement: 0, sheila_length: 0,
  },
  items: [],
  financial: {
    total_price: 0, advance_paid: 0, balance_due: 0,
    payment_method: "cash", is_urgent: false,
  },
  delivery_date: "",
};

export default function NewOrder() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [step, setStep] = useState(0);
  const [orderData, setOrderData] = useState(initialOrder);
  const [fabrics, setFabrics] = useState([]);
  const [collections, setCollections] = useState([]);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  useEffect(() => {
    axios.get(`${API}/fabrics`).then((r) => setFabrics(r.data)).catch(console.error);
    axios.get(`${API}/collections`).then((r) => setCollections(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (editId) {
      setLoadingEdit(true);
      axios.get(`${API}/orders/${editId}`).then((r) => {
        const o = r.data;
        setOrderData({
          customer: o.customer || initialOrder.customer,
          measurements: o.measurements || initialOrder.measurements,
          items: o.items || [],
          financial: o.financial || initialOrder.financial,
          delivery_date: o.delivery_date || "",
        });
      }).catch(() => toast.error("Failed to load order")).finally(() => setLoadingEdit(false));
    }
  }, [editId]);

  const update = (section, values) => {
    setOrderData((prev) => ({
      ...prev,
      [section]: typeof values === "object" && !(values instanceof Date) && values !== null && !Array.isArray(values)
        ? { ...prev[section], ...values }
        : values,
    }));
  };

  const setItems = (itemsOrFn) => {
    setOrderData((prev) => ({
      ...prev,
      items: typeof itemsOrFn === "function" ? itemsOrFn(prev.items) : itemsOrFn,
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const total = orderData.items.reduce((s, i) => s + i.quantity * i.price_per_piece, 0);
      const payload = {
        ...orderData,
        financial: { ...orderData.financial, total_price: total, balance_due: total - orderData.financial.advance_paid },
      };
      if (isEdit) {
        await axios.put(`${API}/orders/${editId}`, payload);
        toast.success("Order updated successfully");
        navigate(`/orders/${editId}`);
      } else {
        const res = await axios.post(`${API}/orders`, payload);
        setCreatedOrder(res.data);
        toast.success(`Order ${res.data.order_number} created successfully`);
      }
    } catch (err) {
      toast.error(isEdit ? "Failed to update order" : "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = () => {
    if (step === 0) return orderData.customer.name.trim() !== "";
    if (step === 1) return true;
    if (step === 2) return orderData.items.length > 0 && orderData.items.every((i) => i.fabric_id);
    if (step === 3) return orderData.items.some((i) => i.price_per_piece > 0);
    return true;
  };

  const renderStep = () => {
    switch (step) {
      case 0: return <CustomerStep data={orderData.customer} update={(v) => update("customer", v)} onMeasurementsUpdate={(m) => update("measurements", m)} />;
      case 1: return <MeasurementsStep data={orderData.measurements} update={(v) => update("measurements", v)} />;
      case 2: return <ItemsStep items={orderData.items} setItems={setItems} fabrics={fabrics} collections={collections} />;
      case 3: return <CheckoutStep data={orderData.financial} update={(v) => update("financial", v)} items={orderData.items} deliveryDate={orderData.delivery_date} setDeliveryDate={(d) => update("delivery_date", d)} />;
      case 4: return <ConfirmationStep data={orderData} createdOrder={createdOrder} onSubmit={handleSubmit} submitting={submitting} isEdit={isEdit} />;
      default: return null;
    }
  };

  if (loadingEdit) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#9F7D23] animate-spin" /></div>;
  }

  if (createdOrder) {
    return (
      <div className="animate-fade-in-up" data-testid="order-created">
        <ConfirmationStep data={orderData} createdOrder={createdOrder} onSubmit={handleSubmit} submitting={submitting} isEdit={isEdit} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="new-order-page">
      <h2 className="font-['Space_Mono'] text-3xl tracking-tight uppercase text-[#5C4D24] mb-2">
        {isEdit ? "Edit Order" : "New Order"}
      </h2>
      <p className="text-sm text-[#8B7536] mb-8">{isEdit ? "Update the order details" : "Complete each step to place an order"}</p>

      <StepIndicator currentStep={step} />

      <div className="bg-white border border-[#D6D6CF] p-6 md:p-8 min-h-[400px]">
        {renderStep()}
      </div>

      {step < 4 && (
        <div className="flex items-center justify-between mt-6">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="step-back-btn" className="flex items-center gap-2 px-6 py-3 font-['Space_Mono'] text-xs tracking-widest uppercase text-[#5C4D24] border border-[#D6D6CF] hover:border-[#9F7D23] disabled:opacity-30 disabled:hover:border-[#D6D6CF] transition-all">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button onClick={() => setStep((s) => Math.min(4, s + 1))} disabled={!canNext()} data-testid="step-next-btn" className="flex items-center gap-2 px-8 py-3 font-['Space_Mono'] text-xs tracking-widest uppercase bg-[#9F7D23] text-white hover:bg-[#8A6D1F] disabled:opacity-30 disabled:hover:bg-[#9F7D23] transition-all active:scale-95">
            {step === 3 ? "Review" : "Next"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
